/**
 * The placeholder grammar a parametrised method is written in.
 *
 * A prescriptive method says "run a sprint review at the end of each sprint". A
 * parametrised one says "run a sprint review at the end of each {{sprint.length}} sprint",
 * and an adoption workshop answers `sprint.length` once for the client. That is the whole
 * mechanism, and it is deliberately the smallest one that works: no expressions, no
 * conditionals, no loops. A method author writing `{{#if agile}}` would be writing a
 * template language, and a template language in a document a client works from is a thing
 * that has to be sandboxed, versioned and explained.
 *
 * This module lives in the contract rather than in either context because both ends of the
 * substitution need it and neither owns it. The workshop scans the text a facilitator wrote
 * to answer "does deleting this parameter strand a reference"; authoring performs the
 * substitution on the derived draft at publish, because that is where the graph is. One
 * grammar, two readers, no second regex to fall out of step with the first.
 *
 * Two properties worth stating, because the substitution runs over text a client will read:
 *
 *  - **An unanswered placeholder is left standing.** `{{sprint.length}}` in the published
 *    document is visibly somebody's unfinished business; a blank is a method with a hole in
 *    it that nobody will notice until it matters.
 *  - **Substitution does not recurse.** A value containing `{{other}}` is inserted
 *    literally. `String.prototype.replace` with a function already guarantees this — the
 *    result of a replacement is never rescanned — and it is what stops a parameter pair
 *    referring to each other from being an infinite loop in a publish.
 */

import { PARAMETER_KEY_PATTERN } from './primitives.js';

/**
 * `{{ key }}` with optional inner whitespace, keys constrained to the same grammar
 * `ParameterKeySchema` accepts.
 *
 * Sharing `PARAMETER_KEY_PATTERN` is the point: a placeholder naming something no parameter
 * could ever be called is not a placeholder, it is prose that happens to contain braces,
 * and it survives the substitution untouched. Two independently written patterns would
 * eventually disagree about which of the two a given string was.
 */
export const PARAMETER_PLACEHOLDER_PATTERN = new RegExp(
  `\\{\\{\\s*(${PARAMETER_KEY_PATTERN})\\s*\\}\\}`,
  'gu',
);

const placeholders = (): RegExp => new RegExp(PARAMETER_PLACEHOLDER_PATTERN);

/**
 * Every parameter key the text refers to, deduplicated, in first-appearance order.
 *
 * A fresh `RegExp` per call, not the shared one: a `g`-flagged regex carries `lastIndex`
 * across calls, so reusing the module-level instance would make the second scan of the same
 * string start halfway through it. The bug is invisible in a unit test that scans once.
 */
export function parameterKeysIn(text: string): readonly string[] {
  const found = new Set<string>();
  for (const match of text.matchAll(placeholders())) {
    const key = match[1];
    if (key !== undefined) found.add(key);
  }
  return [...found];
}

export interface SubstitutionResult {
  readonly text: string;
  /** Placeholders replaced, counting repeats: three `{{tracker}}` in one string is three. */
  readonly substituted: number;
  /** Keys the text referred to and `values` had no answer for, deduplicated. */
  readonly unanswered: readonly string[];
}

/**
 * Replaces every answered placeholder and reports the rest.
 *
 * `values` is a plain record rather than a `Map` so the caller can build it from the wire
 * shape without a conversion, and it is read with `Object.hasOwn`: a parameter legitimately
 * called `constructor` or `toString` would otherwise resolve to a function on
 * `Object.prototype` and be substituted as `function String() { … }` into a client's method
 * document. The key grammar forbids neither name.
 */
export function substituteParameters(
  text: string,
  values: Readonly<Record<string, string>>,
): SubstitutionResult {
  const unanswered = new Set<string>();
  let substituted = 0;
  const result = text.replace(placeholders(), (match, rawKey: string) => {
    if (!Object.hasOwn(values, rawKey)) {
      unanswered.add(rawKey);
      return match;
    }
    const value = values[rawKey];
    if (typeof value !== 'string') {
      unanswered.add(rawKey);
      return match;
    }
    substituted += 1;
    return value;
  });
  return { text: result, substituted, unanswered: [...unanswered] };
}
